import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../DashboardLayout';
import { FoodSafetyProvider, useFoodSafetyContext, FS_SCREENS } from './context/FoodSafetyContext';
import { foodSafetyService } from './services/foodSafetyService';
// import { useMsal } from "@azure/msal-react";
import WelcomeScreen from './components/WelcomeScreen';
import ChecklistInformation from './components/ChecklistInformation';
import PPEChecklist from './components/PPEChecklist';
import GMPChecklist from './components/GMPChecklist';
import PCIChecklist from './components/PCIChecklist';
import SummaryReporting from './components/SummaryReporting';
import Dashboard from './components/Dashboard';
import moment from 'moment';
import FullPageLoader from '../../common/FullPageLoader';

const FoodSafetyMain: React.FC = () => {
    const { state, dispatch } = useFoodSafetyContext();
    const [searchParams] = useSearchParams();
    const resumedTourIdRef = React.useRef<string | null>(null);

    const tourIdParam = searchParams.get('id') || searchParams.get('TourId') || searchParams.get('tourId');

    useEffect(() => {
        if (!tourIdParam || resumedTourIdRef.current === tourIdParam) return;
        resumedTourIdRef.current = tourIdParam;

        const resumeTour = async () => {
            dispatch({ type: 'SET_LOADING', payload: true });
            try {
                const tours = await foodSafetyService.getTourHistory();
                const cleanTourId = tourIdParam.replace(/[{}]/g, "").trim().toLowerCase();
                const tour = (tours || []).find((t: any) => {
                    const id1 = String(t.cr3ea_prod_rajpura_quality_tourid || "").replace(/[{}]/g, "").trim().toLowerCase();
                    const id2 = String(t.cr3ea_prod_qualitytourid || "").replace(/[{}]/g, "").trim().toLowerCase();
                    return id1 === cleanTourId || id2 === cleanTourId;
                });

                const targetTourId = tour?.cr3ea_prod_rajpura_quality_tourid || tour?.cr3ea_prod_qualitytourid || tourIdParam;
                dispatch({ type: 'SET_TOUR_ID', payload: targetTourId });

                let checklistType = tour?.cr3ea_food_safety_checklisttype;
                if (!checklistType && tour?.cr3ea_title) {
                    const title = String(tour.cr3ea_title);
                    if (title.includes("PPE")) checklistType = "PPE Checklist";
                    else if (title.includes("GMP")) checklistType = "GMP Checklist";
                    else if (title.includes("PCI")) checklistType = "PCI Checklist";
                }
                checklistType = checklistType || "PPE Checklist";

                dispatch({
                    type: 'SET_SELECTIONS',
                    payload: {
                        type: checklistType,
                        shift: tour?.cr3ea_shift || "Shift 1"
                    }
                });

                dispatch({
                    type: 'SET_SESSION_DATA',
                    payload: {
                        site: tour?.cr3ea_plantid || "Rajpura",
                        line: tour?.cr3ea_lineno || "Line 1",
                        qaExecutive: tour?.cr3ea_assigned_qa || "",
                        productionIncharge: tour?.cr3ea_shiftexecutiveproduction || "",
                        areaIncharge: tour?.cr3ea_food_safety_areaincharge,
                        area: tour?.cr3ea_food_safety_area || (checklistType === "PCI Checklist" ? "New Block" : undefined),
                        cycle: tour?.cr3ea_food_safety_cycle || "Cycle-1",
                        tourStartDate: tour?.cr3ea_tourstartdate || new Date().toISOString()
                    }
                });

                const status = String(tour?.cr3ea_status || tour?.cr3ea_processstatus || "").trim();
                const isTerminal = status === "Submitted" || status === "Completed" || status === "Closed" || status === "Closed - Expired";

                if (isTerminal) {
                    // Completed tour: show summary report
                    dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.SUMMARY });
                } else {
                    // Active / In-progress tour: resume directly to checklist form stage
                    dispatch({ type: 'SET_IS_PAUSED', payload: true });
                    if (checklistType === "PPE Checklist") {
                        dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.PPE_CHECKLIST });
                    } else if (checklistType === "GMP Checklist") {
                        dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.GMP_CHECKLIST });
                    } else if (checklistType === "PCI Checklist") {
                        dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.PCI_CHECKLIST });
                    } else {
                        dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.CHECKLIST_INFO });
                    }
                }
            } catch (err) {
                console.error("Failed to resume Food Safety session from URL param:", err);
                dispatch({ type: 'SET_SCREEN', payload: FS_SCREENS.CHECKLIST_INFO });
            } finally {
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        };

        resumeTour();
    }, [tourIdParam]);

    const [isOnline, setIsOnline] = React.useState(navigator.onLine);
    const [syncing, setSyncing] = React.useState(false);

    // Auto-sync when coming back online
    useEffect(() => {
        const handleOnline = async () => {
            setIsOnline(true);
            setSyncing(true);
            try {
                await foodSafetyService.syncOfflineData();
            } finally {
                setSyncing(false);
            }
        };

        const handleOffline = () => {
            setIsOnline(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Initial sync attempt if online
        if (navigator.onLine) {
            foodSafetyService.syncOfflineData().catch(e => console.warn("Initial sync check error:", e));
        }

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const renderCurrentState = () => {
        if (state.isLoading) {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading Food Safety...</p>
                </div>
            );
        }

        switch (state.currentScreen) {
            case FS_SCREENS.WELCOME:
                return <WelcomeScreen />;
            case FS_SCREENS.CHECKLIST_INFO:
                return <ChecklistInformation />;
            case FS_SCREENS.PPE_CHECKLIST:
                return <PPEChecklist />;
            case FS_SCREENS.GMP_CHECKLIST:
                return <GMPChecklist />;
            case FS_SCREENS.PCI_CHECKLIST:
                return <PCIChecklist />;
            case FS_SCREENS.SUMMARY:
                return <SummaryReporting />;
            case FS_SCREENS.DASHBOARD:
                return <Dashboard />;
            default:
                return <ChecklistInformation />;
        }
    };

    const isChecklistScreen = state.currentScreen === FS_SCREENS.PPE_CHECKLIST || 
                              state.currentScreen === FS_SCREENS.GMP_CHECKLIST || 
                              state.currentScreen === FS_SCREENS.PCI_CHECKLIST;

    const titleText = isChecklistScreen && state.selectedChecklistType
        ? `${state.selectedChecklistType.toUpperCase()} (RAJPURA)`
        : "FOOD SAFETY CHECKLIST (RAJPURA)";

    const currentDateStr = state.sessionData?.tourStartDate
        ? moment(state.sessionData.tourStartDate).format("DD-MM-YYYY")
        : moment().format("DD-MM-YYYY");

    return (
        <DashboardLayout>
            <FullPageLoader isLoading={state.isLoading} message="Processing Food Safety request... Please wait." />
            <main id="main" className="main" style={{ backgroundColor: '#ffffff', minHeight: '100vh', padding: '20px 24px' }}>
                <div className="container-fluid p-0">
                    
                    {/* Top Shared Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h4 style={{ margin: 0, fontWeight: 700, fontSize: '16px', color: '#0f172a', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
                                {titleText}
                            </h4>
                            {!isOnline && (
                                <span style={{ backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                    <i className="bi bi-cloud-slash"></i> Offline Mode
                                </span>
                            )}
                            {isOnline && syncing && (
                                <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                                    <i className="bi bi-arrow-repeat spin"></i> Syncing...
                                </span>
                            )}
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {state.selectedChecklistType === 'PCI Checklist' && state.sessionData?.area && (
                                <span className="badge" style={{ backgroundColor: '#6366f1', color: '#ffffff', fontWeight: 600, padding: '5px 10px', borderRadius: '4px' }}>
                                    {state.sessionData.area}
                                </span>
                            )}
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 600 }}>
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#15803d' }}></span>
                                {state.selectedShift || 'Shift 1'}
                            </span>
                            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                                • {currentDateStr}
                            </span>
                        </div>
                    </div>

                    {/* Content Screen Area */}
                    <div>
                        {renderCurrentState()}
                    </div>
                </div>
            </main>
        </DashboardLayout>
    );
};

const FoodSafety: React.FC = () => {
    return (
        <FoodSafetyProvider>
            <FoodSafetyMain />
        </FoodSafetyProvider>
    );
};

export default FoodSafety;
